import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Chip,
  Divider,
  IconButton
} from '@mui/material';
import { Close as CloseIcon, Keyboard as KeyboardIcon } from '@mui/icons-material';
import { useThemeSettings } from '../../../../contexts/ThemeContext';
import { useShortcutsHelp } from '../../hooks/useKeyboardShortcuts';

/**
 * Dialog showing available keyboard shortcuts
 */
const ShortcutsDialog = ({ open, onClose }) => {
  const { t } = useThemeSettings();
  const shortcuts = useShortcutsHelp();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <KeyboardIcon color="primary" />
            <span>{t ? t('Common.keyboard_shortcuts') : 'Keyboard Shortcuts'}</span>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {Object.entries(shortcuts).map(([category, items], catIndex) => (
          <Box key={category} sx={{ mb: catIndex < Object.keys(shortcuts).length - 1 ? 3 : 0 }}>
            <Typography variant="overline" color="primary" sx={{ fontWeight: 600 }}>
              {category}
            </Typography>
            <Divider sx={{ my: 1 }} />
            {items.map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 1,
                  '&:hover': { bgcolor: 'action.hover' },
                  px: 1,
                  borderRadius: 1
                }}
              >
                <Typography variant="body2">{item.description}</Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {item.keys.map((key, keyIndex) => (
                    <React.Fragment key={keyIndex}>
                      {keyIndex > 0 && (
                        <Typography variant="caption" sx={{ mx: 0.5, color: 'text.secondary' }}>
                          or
                        </Typography>
                      )}
                      <Chip
                        label={key}
                        size="small"
                        sx={{
                          fontFamily: 'monospace',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          bgcolor: 'action.selected'
                        }}
                      />
                    </React.Fragment>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        ))}

        <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            <strong>{t ? t('Common.tip') : 'Tip'}:</strong>{' '}
            {t ? t('Tickets.shortcuts_tip') : 'Press ? anytime to view this help'}
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ShortcutsDialog;
